import { cardBelongsToCatalogGroup, getCatalogGroup } from './accessUtils';

/**
 * Modules a student can actually see: card and its category are neither
 * archived nor hidden. Admins keep modules archived until a product is ready
 * for sale, so these are what decide whether a product shows in the showcase.
 */
export function getPublishedCatalogCards(allCategories = []) {
  return (allCategories || [])
    .filter((category) => !category.archived && !category.hideContent)
    .flatMap((category) =>
      (category.cards || []).filter((card) => !card.archived && !card.hideContent)
    );
}

/** The showcase product has at least one published module linked to its group. */
export function catalogProductHasPublishedModule(product, publishedCards = []) {
  const group = getCatalogGroup(product);
  return publishedCards.some((card) => cardBelongsToCatalogGroup(card, group));
}

/** Categorias visíveis de um produto de vitrine (antes de filtro por tag). */
export function buildProductCategories(allCategories, group, user) {
  return (allCategories || [])
    .map((category) => {
      const allCards = category.cards || [];
      const productCards = allCards.filter((card) =>
        cardBelongsToCatalogGroup(card, group)
      );
      const categoryTouchesProduct = productCards.length > 0;
      const allCardsAreDraft = allCards.length > 0 && allCards.every((card) => card.archived);

      let visibleCards;
      if (user?.isAdmin && categoryTouchesProduct) {
        visibleCards = allCards;
      } else if (user?.isAdmin && allCardsAreDraft) {
        // Categoria/módulo novo ainda sem vínculo ao produto.
        visibleCards = allCards;
      } else {
        visibleCards = productCards.filter(
          (card) => !card.archived && !card.hideContent
        );
      }

      return { ...category, cards: visibleCards };
    })
    .filter((category) => category.cards.length > 0);
}

/** Lista plana de módulos do produto com metadados de navegação. */
export function getProductCatalogModuleEntries(productCategories) {
  return productCategories.flatMap((category) =>
    category.cards.map((card) => ({
      card,
      categoryId: category.id,
      orientation: category.orientation,
    }))
  );
}

/** Aplica filtro por tag no escopo do produto (mesma regra da Main). */
export function applyProductCatalogFilter(productCategories, filterValue) {
  return productCategories
    .map((category) => {
      let cards = category.cards;

      if (category.filterIds?.includes(filterValue)) {
        // Categoria marcada com o filtro: mantém todos os módulos dela.
      } else {
        cards = cards.filter((card) => card.filterIds?.includes(filterValue));
      }

      return { ...category, cards };
    })
    .filter((category) => category.cards.length > 0);
}

/** Aplica filtro configurado na aba Produtos (escopo por produto). */
export function applyProductScopedCatalogFilter(productCategories, filter) {
  if (!filter) return productCategories;

  const categoryIds = new Set(filter.categoryIds || []);
  const cardKeys = new Set(
    (filter.cardEntries || []).map((entry) => `${entry.categoryId}:${entry.cardId}`)
  );

  return productCategories
    .map((category) => {
      if (categoryIds.has(category.id)) {
        return category;
      }

      const cards = category.cards.filter((card) =>
        cardKeys.has(`${category.id}:${card.id}`)
      );

      return { ...category, cards };
    })
    .filter((category) => category.cards.length > 0);
}

const sortedCardIdsKey = (categories) =>
  categories
    .flatMap((category) => category.cards.map((card) => card.id))
    .sort()
    .join(',');

/** Filtro que não muda a lista visível (ex.: tag na categoria que já libera tudo). */
export function filterHasEffectOnProductCatalog(productCategories, filterValue) {
  const baseline = sortedCardIdsKey(productCategories);
  const filtered = sortedCardIdsKey(applyProductCatalogFilter(productCategories, filterValue));
  return baseline !== filtered;
}

/** Chaves `${categoryId}:${cardId}` dos módulos visíveis no catálogo do produto. */
export function getProductCatalogCardKeys(productCategories = []) {
  const keys = [];
  productCategories.forEach((category) => {
    (category.cards || []).forEach((card) => {
      keys.push(`${category.id}:${card.id}`);
    });
  });
  return keys;
}

/** Filtro escopado ao produto que não muda a lista visível. */
export function filterHasEffectOnScopedProductCatalog(productCategories, filter) {
  const baseline = sortedCardIdsKey(productCategories);
  const filtered = sortedCardIdsKey(applyProductScopedCatalogFilter(productCategories, filter));
  return baseline !== filtered;
}

/** Filtros com conteúdo no produto e que realmente restringem a vitrine. */
export function getEffectiveProductCatalogFilters(filters, productCategories) {
  return (filters || []).filter((filter) => {
    const hasContent = productCategories.some((category) => {
      if (category.filterIds?.includes(filter.value)) return true;
      return category.cards.some((card) => card.filterIds?.includes(filter.value));
    });

    if (!hasContent) return false;
    return filterHasEffectOnProductCatalog(productCategories, filter.value);
  });
}

/** Filtros do produto (aba Produtos) com conteúdo e efeito real na listagem. */
export function getEffectiveProductScopedCatalogFilters(filters, productCategories) {
  return (filters || []).filter((filter) => {
    if (filter.archived) return false;

    const hasContent = productCategories.some((category) => {
      if ((filter.categoryIds || []).includes(category.id)) return true;
      return (filter.cardEntries || []).some(
        (entry) =>
          entry.categoryId === category.id &&
          category.cards.some((card) => card.id === entry.cardId)
      );
    });

    if (!hasContent) return false;
    return filterHasEffectOnScopedProductCatalog(productCategories, filter);
  });
}

export function mapProductScopedFiltersForBar(filters) {
  return (filters || [])
    .filter((filter) => !filter.archived)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((filter) => ({
      label: filter.name,
      value: filter.slug,
    }));
}

export const CATALOG_COLLECTION_BY_TYPE = {
  product: 'products',
  plan: 'plans',
  course: 'courses',
};
